import React, {FC, memo} from "react";
import {Alert, Descriptions, Tag} from "antd";
import moment from "moment";

type PricingSnapshot = {
    currency?: unknown;
    originalGrossAmountInCents?: unknown;
    serviceGrossAmountInCents?: unknown;
    reductionInCents?: unknown;
    totalLessonCount?: unknown;
    pricedLessonCount?: unknown;
    pricedAt?: unknown;
};

export type LateEntryPricingNoticeProps = {
    pricingReason?: string | null;
    pricingCalculatedAt?: string | Date | null;
    pricingSnapshot?: unknown;
    compact?: boolean;
};

const isSafeInteger = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value);

const isPositiveInteger = (value: unknown): value is number =>
    isSafeInteger(value) && value > 0;

const asSnapshot = (value: unknown): PricingSnapshot | undefined => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return undefined;
    }

    return value as PricingSnapshot;
};

const currencyCode = (value: unknown): string => {
    switch (value) {
        case "CHF":
        case "USD":
        case "GBP":
        case "EUR":
            return value;
        default:
            return "EUR";
    }
};

const formatCents = (value: number, currency: string): string =>
    new Intl.NumberFormat(currency === "CHF" ? "de-CH" : "de-DE", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value / 100);

const isAdjustedPricingReason = (value: string | null | undefined): boolean =>
    value === "LATE_ENTRY_REMAINING_LESSONS" ||
    value === "EXPLICIT_LESSON_SELECTION";

const LateEntryPricingNotice: FC<LateEntryPricingNoticeProps> = ({
    pricingReason,
    pricingCalculatedAt,
    pricingSnapshot,
    compact = false,
}) => {
    if (!isAdjustedPricingReason(pricingReason)) {
        return null;
    }

    const snapshot = asSnapshot(pricingSnapshot);
    const totalLessonCount = snapshot?.totalLessonCount;
    const pricedLessonCount = snapshot?.pricedLessonCount;
    const originalGrossAmountInCents = snapshot?.originalGrossAmountInCents;
    const serviceGrossAmountInCents = snapshot?.serviceGrossAmountInCents;
    const reductionInCents = snapshot?.reductionInCents;
    const currency = currencyCode(snapshot?.currency);
    const hasLessonCounts =
        isPositiveInteger(totalLessonCount) &&
        isPositiveInteger(pricedLessonCount) &&
        pricedLessonCount <= totalLessonCount;
    const hasAmounts =
        isSafeInteger(originalGrossAmountInCents) &&
        originalGrossAmountInCents >= 0 &&
        isSafeInteger(serviceGrossAmountInCents) &&
        serviceGrossAmountInCents >= 0 &&
        isSafeInteger(reductionInCents) &&
        reductionInCents >= 0;
    const calculatedAt = pricingCalculatedAt ?? snapshot?.pricedAt;
    const calculatedAtMoment =
        typeof calculatedAt === "string" || calculatedAt instanceof Date
            ? moment(calculatedAt)
            : undefined;
    const title =
        pricingReason === "EXPLICIT_LESSON_SELECTION"
            ? "Preis für ausgewählte Kurseinheiten berechnet"
            : "Preis wegen spätem Einstieg angepasst";

    if (compact) {
        return (
            <Tag color="gold" data-testid="adjusted-booking-price-tag">
                {pricingReason === "EXPLICIT_LESSON_SELECTION"
                    ? "Einzelauswahl"
                    : "Später Einstieg"}
            </Tag>
        );
    }

    return (
        <Alert
            data-testid="late-entry-pricing-notice"
            type="info"
            showIcon
            message={title}
            style={{marginBottom: 16}}
            description={
                <div>
                    <p style={{marginTop: 0}}>
                        {hasLessonCounts
                            ? `${pricedLessonCount} von ${totalLessonCount} Kurseinheiten wurden bei der Preisberechnung berücksichtigt.`
                            : "Für diese Buchung wurde ein angepasster Kurspreis berechnet."}
                    </p>
                    {hasAmounts ? (
                        <Descriptions size="small" column={3} colon={false}>
                            <Descriptions.Item label="Originalpreis">
                                {formatCents(originalGrossAmountInCents, currency)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Reduzierung">
                                {formatCents(reductionInCents, currency)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Preis der berücksichtigten Einheiten">
                                {formatCents(serviceGrossAmountInCents, currency)}
                            </Descriptions.Item>
                        </Descriptions>
                    ) : null}
                    {calculatedAtMoment?.isValid() ? (
                        <small>
                            {`Unveränderlicher Berechnungsstand vom ${calculatedAtMoment.format("L LT")} Uhr. Weitere Rabatte und Guthaben werden im Buchungspreis separat berücksichtigt.`}
                        </small>
                    ) : null}
                </div>
            }
        />
    );
};

export default memo(LateEntryPricingNotice);
